import { useCallback, useMemo, useRef, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { GetWorkspaceUsersDocument } from "#app/lib/data";
import { type EntityItemConfig, type EntityPoolConfig } from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { resolveEntityLabel, type EntityData } from "#app/lib/entity-visuals";
import { useEntitySearchPool } from "./use-entity-search-pool";
import type { Omittable } from "@salestrak/ui";

const PAGE_SIZE = 50;
const ALL_STATUSES = ["active", "invited"] as const;
const ACTIVE_ONLY_STATUSES = ["active"] as const;

const POOL_ID = "users";

// THE user → selector-item mapping (EntityTag visual render, invited/suspended
// treatment, label fallback chain). Shared by every selector/filter listing users.
export function userToPoolItem(node: {
  userId: string;
  name?: string | null;
  email?: string | null;
  profileImage?: string | null;
  memberStatus?: string | null;
}): EntityItemConfig {
  const email = node.email ?? "";
  const entity: EntityData = {
    kind: "user",
    id: node.userId,
    user: {
      name: node.name,
      email,
      profileImage: node.profileImage,
      memberStatus: node.memberStatus,
    },
  };
  const invited = node.memberStatus === "invited";
  return {
    id: node.userId,
    title: node.name?.trim() || resolveEntityLabel(entity) || node.userId,
    description: invited ? "Invited" : undefined,
    invited,
    avatar: {
      src: node.profileImage ?? undefined,
      render: () => <EntityTag entity={entity} display="visual" interactive={false} />,
    },
    keywords: email ? [email] : [],
    // memberStatus is stashed here so optimistic-response builders can
    // round-trip the real value back into the Apollo cache write (the
    // *EntityRef fragments select User.memberStatus, and Apollo warns
    // when the optimistic value is missing a selected field).
    data: { email, entityType: "user", memberStatus: node.memberStatus ?? null },
  };
}

export type UseUserPoolOptions = Omittable<{
  /** User IDs to exclude from the pool (e.g., already-assigned members) */
  exclude?: string[];
  /** When true, only "active" members are returned. Defaults to including "invited" members so assignment selectors can target pending users. Filter selectors should opt in. */
  activeOnly?: boolean;
}>;

/**
 * Fetches workspace users and returns an EntityPoolConfig for use with EntitySelector.
 * Loads the first page on mount; additional pages load on demand via
 * `onLoadRange` as the user scrolls. Supports server-side search via
 * `onSearchChange` to find members not yet loaded.
 */
export function useUserPool(options?: UseUserPoolOptions) {
  const { exclude, activeOnly } = options ?? {};
  const client = useApolloClient();

  const memberStatusFilter = useMemo(
    () => ({
      where: {
        memberStatus: {
          in: [...(activeOnly ? ACTIVE_ONLY_STATUSES : ALL_STATUSES)],
        },
      },
    }),
    [activeOnly],
  );

  const initialQueryVars = useMemo(() => ({ limit: PAGE_SIZE, skip: 0, ...memberStatusFilter }), [memberStatusFilter]);

  const { data, loading } = useQuery(GetWorkspaceUsersDocument, {
    variables: initialQueryVars,
  });

  const total: number = data?.users?.total ?? 0;

  const initialItems = useMemo(() => {
    if (!data?.users?.edges) return [];
    return data.users.edges.filter((edge) => edge?.node).map((edge) => userToPoolItem(edge.node));
  }, [data]);

  const extraMapRef = useRef(new Map<number, EntityItemConfig>());
  const [extraVersion, setExtraVersion] = useState(0);
  const fetchingRef = useRef<Set<number>>(new Set());
  const attemptedPagesRef = useRef<Set<number>>(new Set());
  const epochRef = useRef(0);

  // Reset lazily-loaded pages only when the FILTER changes — NOT when Apollo
  // re-emits `data`. `User` is a normalized, app-wide-shared cache entity, so
  // the base query's `data` reference churns constantly as unrelated queries
  // write Users (hover previews, workspace data, issue lists, …). Keying the
  // reset on that identity wiped every loaded page on each churn, so scrolling
  // showed permanent skeletons: pages were refetched but discarded before they
  // could render. memberStatusFilter is a stable memo that only changes when
  // the filter (activeOnly) actually changes — the one time stale absolute-index
  // pages truly must be dropped. Ref mutations only — no setState — to avoid
  // loops with cache re-emission. baseItems recomputes via initialItems dep.
  const filterEpoch = useRef(memberStatusFilter);
  if (filterEpoch.current !== memberStatusFilter) {
    filterEpoch.current = memberStatusFilter;
    epochRef.current += 1;
    extraMapRef.current = new Map();
    fetchingRef.current.clear();
    attemptedPagesRef.current = new Set();
  }
  if (data?.users?.edges) {
    attemptedPagesRef.current.add(0);
  }

  const loadRange = useCallback(
    (startIndex: number, endIndex: number) => {
      const lastNeeded = Math.min(total - 1, endIndex);
      const startPage = Math.floor(startIndex / PAGE_SIZE);
      const lastPage = Math.ceil(total / PAGE_SIZE) - 1;
      const endPage = Math.min(lastPage, Math.floor(lastNeeded / PAGE_SIZE) + 1);

      for (let page = startPage; page <= endPage; page++) {
        if (page === 0) continue;
        const skip = page * PAGE_SIZE;
        if (attemptedPagesRef.current.has(page) || fetchingRef.current.has(skip)) continue;

        const fetchEpoch = epochRef.current;
        fetchingRef.current.add(skip);
        client
          .query({
            query: GetWorkspaceUsersDocument,
            variables: { skip, limit: PAGE_SIZE, ...memberStatusFilter },
            fetchPolicy: "no-cache",
          })
          .then(({ data: result }) => {
            if (epochRef.current !== fetchEpoch) return;
            attemptedPagesRef.current.add(page);
            const edges = result?.users?.edges;
            if (edges) {
              const currentMap = extraMapRef.current;
              edges.forEach((edge, i) => {
                if (edge?.node) {
                  currentMap.set(skip + i, userToPoolItem(edge.node));
                }
              });
            }
            setExtraVersion((v) => v + 1);
          })
          .catch((err) => console.warn("[useUserPool] Failed to load page:", err))
          .finally(() => {
            fetchingRef.current.delete(skip);
          });
      }
    },
    [total, client, memberStatusFilter],
  );

  const baseItems = useMemo<EntityItemConfig[]>(() => {
    const seen = new Set<string>();
    const all: EntityItemConfig[] = [];
    for (const item of initialItems) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        all.push(item);
      }
    }
    extraMapRef.current.forEach((item) => {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        all.push(item);
      }
    });
    return all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItems, extraVersion]);

  const runSearch = useCallback(
    async (trimmed: string): Promise<EntityItemConfig[]> => {
      const { data: result } = await client.query({
        query: GetWorkspaceUsersDocument,
        variables: {
          limit: PAGE_SIZE,
          skip: 0,
          where: {
            ...memberStatusFilter.where,
            search: trimmed,
          },
        },
        fetchPolicy: "no-cache",
      });
      if (!result?.users?.edges) return [];
      return result.users.edges.filter((edge) => edge?.node).map((edge) => userToPoolItem(edge.node));
    },
    [client, memberStatusFilter],
  );

  const { items, onSearchChange } = useEntitySearchPool({
    baseItems,
    runSearch,
    exclude,
    // Reset cached server-search results on filter change, not on every `data`
    // re-emission — same churn problem as the paged extras above.
    epoch: memberStatusFilter,
  });

  const lastPageNeeded = total > 0 ? Math.ceil(total / PAGE_SIZE) - 1 : -1;
  let allPagesAttempted = total > 0;
  for (let page = 0; page <= lastPageNeeded; page++) {
    if (!attemptedPagesRef.current.has(page)) {
      allPagesAttempted = false;
      break;
    }
  }

  const excludeSize = exclude?.length ? new Set(exclude).size : 0;
  const adjustedTotal = allPagesAttempted
    ? items.length
    : excludeSize > 0
      ? Math.max(total - excludeSize, items.length)
      : total;

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Members",
      items,
      total: adjustedTotal,
      onLoadRange: loadRange,
      onSearchChange,
      // Server-filtered already; client-filtering would hide server matches not in the initial pages.
      serverFiltered: true,
    }),
    [items, adjustedTotal, loadRange, onSearchChange],
  );

  return { pool, loading };
}

export { POOL_ID as USER_POOL_ID };
